import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import PageWrapper from "@/components/layout/PageWrapper";

type LegalSection = {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
};

type PrivacyContent = {
  pageTitle: string;
  metaTitle: string;
  metaDesc: string;
  intro: string;
  updatedAt: string;
  sections: LegalSection[];
};

export default function PrivacyPolicyPage() {
  const { t } = useTranslation();
  const content = t("legal.privacy", { returnObjects: true }) as PrivacyContent;

  return (
    <PageWrapper>
      <title>{content.metaTitle}</title>
      <meta name="description" content={content.metaDesc} />

      <section className="w-full bg-[#F5F3EF] pt-28 md:pt-36 pb-12 md:pb-16">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <motion.div
            className="max-w-4xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold text-stone-900 mb-4 leading-tight">
              {content.pageTitle}
            </h1>
            <p className="text-base md:text-lg text-stone-600 leading-relaxed mb-4">
              {content.intro}
            </p>
            <p className="text-sm text-stone-500">{content.updatedAt}</p>
          </motion.div>
        </div>
      </section>

      <section className="w-full bg-white py-14 md:py-20">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl space-y-8 md:space-y-10">
            {content.sections.map((section, index) => (
              <motion.article
                key={section.title}
                className="rounded-2xl border border-stone-200 bg-white p-6 md:p-8"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: index * 0.04 }}
              >
                <h2 className="text-2xl md:text-3xl font-bold text-stone-900 mb-4">
                  {section.title}
                </h2>

                {section.paragraphs?.map((paragraph) => (
                  <p
                    key={paragraph}
                    className="text-base md:text-lg text-stone-700 leading-relaxed mb-3 last:mb-0"
                  >
                    {paragraph}
                  </p>
                ))}

                {!!section.bullets?.length && (
                  <ul className="mt-4 space-y-2">
                    {section.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-3 text-stone-700">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-brand flex-shrink-0" />
                        <span className="text-base md:text-lg leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </PageWrapper>
  );
}
